import {useAccessContext} from '../../context/AccessContext'
import {useI18n} from '../../i18n/I18nContext'
import {SupervisorOperationsPanel} from './supervisor/SupervisorOperationsPanel'
import React, { useEffect, useMemo, useState } from 'react'
import { adminService } from '../../services/admin'
import { useAuth } from '../../context/AuthContext'
import type { LiveWorkerLocationDto, PaginationMeta } from '../../types/access'
import { PaginationControls } from './admin/AdminUiPrimitives'
import { ShiftHistoryViewer } from './admin/ShiftHistoryViewer'
import AssignmentLifecycleBadge from './AssignmentLifecycleBadge'

type SupervisorLocation = {
  id?: string
  key: string
  label?: string
  name?: string
}

type LogRow = {
  id?: string
  timestamp?: string
  action?: string
  name?: string
  phone?: string
  allowed?: string
  message?: string
}

type BreadcrumbRow = {
  id?: string
  recordedAt?: string
  phone?: string
  workerName?: string
  lat?: number | null
  lon?: number | null
  accuracy?: number | null
  source?: string
}

type TimesheetRow = {
  shiftId: string
  workerId: string
  workerName: string
  workerEmail: string
  date: string
  punchIn: string
  breakStart: string
  breakEnd: string
  breakMinutes: number
  punchOut: string
  paidHours: number
  adjusted: boolean
  approved: boolean
  notes: string
}

type AdminMessageRow = {
  id: string
  body: string
  sentAt: string
  locationName: string
  workerEmail: string
  workerPhone: string
  deliveryState: string
}

const formatPhone = (value?: string) => {
  const digits = (value || '').replace(/\D/g, '')
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  return value || '-'
}

const formatTime = (value?: string) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })
}

const formatDuration = (seconds?: number) => {
  if (!seconds || seconds < 0) return '-'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${String(minutes).padStart(2, '0')}m`
}

const workerId = (worker: LiveWorkerLocationDto) => worker.userId || worker.workerProfileId || worker.globalWorkerProfileId || worker.worker_profile_id || ''
const workerName = (worker: LiveWorkerLocationDto) => worker.displayName || worker.workerName || worker.worker_name || worker.name || worker.email || worker.phone || 'Worker'
const workerShiftStart = (worker: LiveWorkerLocationDto) => worker.shiftStartedAt || worker.shift_started_at || worker.startedAt || worker.started_at || ''
const workerScheduleLabel = (worker: LiveWorkerLocationDto) => {
  if (worker.isLate || worker.late || worker.scheduleStatus === 'late' || worker.timeState === 'late') return 'Late'
  const status = String(worker.status || '').toLowerCase()
  if (status === 'punched_in') return 'Live'
  if (status === 'break') return 'Live / break'
  return 'Scheduled'
}
const activeDurationSeconds = (worker: LiveWorkerLocationDto) => {
  const start = workerShiftStart(worker)
  const parsed = Date.parse(start)
  if (!Number.isFinite(parsed)) return 0
  return Math.max(0, Math.floor((Date.now() - parsed) / 1000))
}
const liveWorkerPagination = (total: number, page: number, pageSize: number): PaginationMeta => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const safePage = Math.min(Math.max(1, page), totalPages)
  return {
    page: safePage,
    pageSize,
    total,
    totalPages,
    hasNextPage: safePage < totalPages,
    hasPreviousPage: safePage > 1,
  }
}

const today = new Date().toISOString().slice(0, 10)
const weekAgo = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)

const SupervisorLiveMap: React.FC = () => {
  const { user } = useAuth()
  const [locations, setLocations] = useState<SupervisorLocation[]>([])
  const [selectedLocation, setSelectedLocation] = useState('')
  const [logs, setLogs] = useState<LogRow[]>([])
  const [messageBody, setMessageBody] = useState('')
  const [directPhone, setDirectPhone] = useState('')
  const [approvalNotes, setApprovalNotes] = useState('')
  const [selectedLogId, setSelectedLogId] = useState('')
  const [breadcrumbPhone, setBreadcrumbPhone] = useState('')
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbRow[]>([])
  const [liveWorkers, setLiveWorkers] = useState<LiveWorkerLocationDto[]>([])
  const [liveWorkerPage, setLiveWorkerPage] = useState(1)
  const [liveWorkerPageSize, setLiveWorkerPageSize] = useState(10)
  const [punchOutReason, setPunchOutReason] = useState('Manual admin punch-out')
  const [timesheetFrom, setTimesheetFrom] = useState(weekAgo)
  const [timesheetTo, setTimesheetTo] = useState(today)
  const [timesheets, setTimesheets] = useState<TimesheetRow[]>([])
  const [timesheetSummary, setTimesheetSummary] = useState<any[]>([])
  const [workerFilter, setWorkerFilter] = useState('')
  const [messageAudience, setMessageAudience] = useState<'location' | 'active' | 'worker'>('location')
  const [messageWorkerId, setMessageWorkerId] = useState('')
  const [messageHistory, setMessageHistory] = useState<AdminMessageRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const loadLocations = async () => {
    const response = await adminService.getLocations()
    const nextLocations = (response?.data?.locations || []) as SupervisorLocation[]
    setLocations(nextLocations)
    if (nextLocations.length > 0) {
      setSelectedLocation((current) => current || nextLocations[0].key)
    }
  }

  useEffect(() => {
    setLoading(true)
    loadLocations()
      .catch((err: any) => setError(err?.response?.data?.message || 'Unable to load locations.'))
      .finally(() => setLoading(false))
  }, [])

  const loadDashboard = async (locationKey: string) => {
    if (!locationKey) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.getSupervisorDashboard(locationKey)
      setLogs((response.data.logs || []) as LogRow[])
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load supervisor dashboard.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDashboard(selectedLocation)
  }, [selectedLocation])

  const selectedLocationId = useMemo(() => locations.find((entry) => entry.key === selectedLocation)?.id || '', [locations, selectedLocation])

  const selectedLog = useMemo(() => logs.find((log) => log.id === selectedLogId), [logs, selectedLogId])
  const liveWorkerStats = useMemo(() => ({
    totalWorkers: liveWorkers.length,
    punchedIn: liveWorkers.filter((worker) => String(worker.status || '').toLowerCase() === 'punched_in').length,
    onBreak: liveWorkers.filter((worker) => String(worker.status || '').toLowerCase() === 'break').length,
    late: liveWorkers.filter((worker) => worker.isLate || worker.late || worker.scheduleStatus === 'late' || worker.timeState === 'late').length,
  }), [liveWorkers])
  const liveWorkerPageMeta = useMemo(
    () => liveWorkerPagination(liveWorkers.length, liveWorkerPage, liveWorkerPageSize),
    [liveWorkers.length, liveWorkerPage, liveWorkerPageSize],
  )
  const paginatedLiveWorkers = useMemo(() => {
    const start = (liveWorkerPageMeta.page - 1) * liveWorkerPageMeta.pageSize
    return liveWorkers.slice(start, start + liveWorkerPageMeta.pageSize)
  }, [liveWorkers, liveWorkerPageMeta.page, liveWorkerPageMeta.pageSize])

  const sendBroadcast = async () => {
    if (!selectedLocation || !messageBody.trim()) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.sendLocationMessage({ locationKey: selectedLocation, message: messageBody, audience: 'ALL' })
      setStatus(`${response.data.sentCount || 0} messages sent.`)
      setMessageBody('')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to send message.')
    } finally {
      setLoading(false)
    }
  }

  const sendDirect = async () => {
    if (!selectedLocation || !directPhone || !messageBody.trim()) return
    setLoading(true)
    setError('')
    try {
      await adminService.sendSupervisorDirectMessage({ locationKey: selectedLocation, phone: directPhone, message: messageBody })
      setStatus('Direct message sent.')
      setDirectPhone('')
      setMessageBody('')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to send direct message.')
    } finally {
      setLoading(false)
    }
  }

  const approveSelected = async (allowed: boolean) => {
    if (!selectedLog?.phone || !selectedLog.timestamp) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.updatePunchApproval({
        phone: selectedLog.phone,
        timestamp: selectedLog.timestamp,
        allowed,
        notes: approvalNotes,
      })
      setStatus(response.data.message || 'Punch approval saved.')
      setApprovalNotes('')
      await loadDashboard(selectedLocation)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save approval.')
    } finally {
      setLoading(false)
    }
  }

  const loadBreadcrumbs = async () => {
    if (!selectedLocation) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.listSupervisorBreadcrumbs({
        locationKey: selectedLocation,
        supervisorEmail: user?.email,
        phone: breadcrumbPhone,
        limit: 50,
      })
      setBreadcrumbs((response.data.breadcrumbs || []) as BreadcrumbRow[])
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load breadcrumbs.')
    } finally {
      setLoading(false)
    }
  }

  const loadOperationalParity = async () => {
    if (!selectedLocationId) return
    setLoading(true)
    setError('')
    try {
      const [liveWorkersResponse, timesheetResponse, messagesResponse] = await Promise.all([
        adminService.listLiveWorkersForLocation(selectedLocationId, { includeLatestOnly: true }),
        adminService.getLocationTimesheets(selectedLocationId, { from: timesheetFrom, to: timesheetTo, workerId: workerFilter || undefined }),
        adminService.listLocationAdminMessages(selectedLocationId),
      ])
      setLiveWorkers((liveWorkersResponse.data?.data || []) as LiveWorkerLocationDto[])
      setLiveWorkerPage(1)
      setTimesheets((timesheetResponse.data.rows || []) as TimesheetRow[])
      setTimesheetSummary(timesheetResponse.data.summary || [])
      setMessageHistory((messagesResponse.data.messages || []) as AdminMessageRow[])
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load operational data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOperationalParity()
  }, [selectedLocationId])

  useEffect(() => {
    if (messageAudience === 'worker' && messageWorkerId && !liveWorkers.some((worker) => workerId(worker) === messageWorkerId)) {
      setMessageWorkerId('')
    }
    if (workerFilter && !liveWorkers.some((worker) => workerId(worker) === workerFilter)) {
      setWorkerFilter('')
    }
  }, [liveWorkers, messageAudience, messageWorkerId, workerFilter])

  const punchOutWorker = async (workerId: string) => {
    if (!selectedLocationId || !workerId) return
    setLoading(true)
    setError('')
    try {
      await adminService.punchOutWorker(selectedLocationId, workerId, punchOutReason)
      setStatus('Worker punched out.')
      await loadOperationalParity()
      await loadDashboard(selectedLocation)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to punch out worker.')
    } finally {
      setLoading(false)
    }
  }

  const approveShift = async (shiftId: string) => {
    if (!selectedLocationId || !shiftId) return
    setLoading(true)
    setError('')
    try {
      await adminService.approveTimesheetShift(selectedLocationId, shiftId)
      setStatus('Timesheet shift approved.')
      await loadOperationalParity()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to approve shift.')
    } finally {
      setLoading(false)
    }
  }

  const downloadExport = async (format: 'csv' | 'xlsx' | 'pdf') => {
    if (!selectedLocationId) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.downloadTimesheetExport(selectedLocationId, format, { from: timesheetFrom, to: timesheetTo, workerId: workerFilter || undefined })
      const url = URL.createObjectURL(response.data)
      window.open(url, '_blank', 'noopener,noreferrer')
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to generate export.')
    } finally {
      setLoading(false)
    }
  }

  const sendAdminMessage = async () => {
    if (!selectedLocationId || !messageBody.trim()) return
    setLoading(true)
    setError('')
    try {
      const payload = {
        locationId: selectedLocationId,
        audience: messageAudience,
        workerId: messageAudience === 'worker' ? messageWorkerId : undefined,
        message: messageBody,
      }
      const response = await adminService.sendAdminMessage(payload)
      setStatus(`${response.data.sentCount || 0} messages sent.`)
      setMessageBody('')
      await loadOperationalParity()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to send admin message.')
    } finally {
      setLoading(false)
    }
  }

  const refreshSupervisorWorkspace = async () => {
    await Promise.all([
      loadDashboard(selectedLocation),
      loadOperationalParity(),
    ])
  }

  return (
    <section className="panel workspace-panel stack" data-testid="supervisor-map-panel">
      <div className="stack" style={{ gap: '.4rem' }}>
        <h2 className="heading-lg">Location supervisor</h2>
        <p className="text-muted">Review worker statuses, messages, recent punch activity, and approval decisions.</p>
      </div>

      <div className="inline-group">
        <select className="input" style={{ maxWidth: 320 }} value={selectedLocation} aria-label="Assigned location" onChange={(event) => setSelectedLocation(event.target.value)} disabled={locations.length === 0 || loading}>
          {locations.length === 0 ? <option value="">No locations available</option> : locations.map((location) => <option key={location.key} value={location.key}>{location.label || location.name || location.key}</option>)}
        </select>
        <button type="button" className="btn btn-secondary" onClick={refreshSupervisorWorkspace} disabled={!selectedLocation || loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={loadOperationalParity} disabled={!selectedLocationId || loading}>Sync operations</button>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {status ? <p className="helper">{status}</p> : null}

      <div className="supervisor-stats">
        <article className="panel stat-card"><strong>{liveWorkerStats.totalWorkers}</strong><span className="helper">Live / scheduled</span></article>
        <article className="panel stat-card"><strong>{liveWorkerStats.punchedIn}</strong><span className="helper">Punched in</span></article>
        <article className="panel stat-card"><strong>{liveWorkerStats.onBreak}</strong><span className="helper">On break</span></article>
        <article className="panel stat-card"><strong>{liveWorkerStats.late}</strong><span className="helper">Late</span></article>
      </div>

      <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
        <div className="mapper-section-heading">
          <div>
            <h3 style={{ margin: 0 }}>Live / scheduled workers</h3>
            <p className="helper">Only workers returned by the live map contract are shown: punched-in, on-break, on-the-way, late, or currently scheduled for this location.</p>
          </div>
          <input className="input" style={{ maxWidth: 320 }} value={punchOutReason} onChange={(event) => setPunchOutReason(event.target.value)} aria-label="Punch-out reason" />
        </div>
        <div className="panel" style={{ overflowX: 'auto' }}>
          <table className="workspace-table" data-testid="supervisor-live-workers-table">
            <thead><tr><th>Worker</th><th>Email</th><th>Status</th><th>Lifecycle / ETA</th><th>Schedule</th><th>On the way</th><th>Shift start</th><th>Active duration</th><th>Action</th></tr></thead>
            <tbody>
              {liveWorkers.length === 0 ? (
                <tr><td colSpan={9} className="helper">No live or scheduled workers found for this location.</td></tr>
              ) : paginatedLiveWorkers.map((worker) => (
                <tr key={workerId(worker) || worker.workerProfileId}>
                  <td>{workerName(worker)}<div className="helper">{formatPhone(worker.phone || worker.workerPhone || worker.worker_phone || '')}</div></td>
                  <td>{worker.email || '-'}</td>
                  <td><span className={`status-pill status-pill--${String(worker.status || '').replace(/[^a-z0-9_-]/gi, '_')}`}>{String(worker.status || 'scheduled').replace('_', ' ')}</span></td>
                  <td>
                    <AssignmentLifecycleBadge
                      status={worker.lifecycleStatus || worker.presence?.lifecycleStatus}
                      exception={worker.attendanceException || worker.presence?.attendanceException}
                      eta={worker.eta || worker.presence?.eta}
                      updatedAt={worker.lifecycleUpdatedAt || worker.presence?.lifecycleUpdatedAt}
                      testId="supervisor-worker-lifecycle"
                    />
                  </td>
                  <td><span className="admin-chip">{workerScheduleLabel(worker)}</span></td>
                  <td>{worker.onTheWayAt ? <span className="admin-chip">{worker.onTheWayEtaLabel ? `ETA ${worker.onTheWayEtaLabel}` : 'On the way · ETA unavailable'}</span> : <span className="helper">Not marked</span>}</td>
                  <td>{formatTime(workerShiftStart(worker))}</td>
                  <td>{formatDuration(activeDurationSeconds(worker))}</td>
                  <td>
                    <button type="button" className="btn btn-secondary" onClick={() => punchOutWorker(workerId(worker))} disabled={!['punched_in', 'break'].includes(String(worker.status || '').toLowerCase()) || !workerId(worker) || loading}>Punch out</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls
          pagination={liveWorkerPageMeta}
          onPageChange={setLiveWorkerPage}
          onPageSizeChange={(pageSize) => {
            setLiveWorkerPageSize(pageSize)
            setLiveWorkerPage(1)
          }}
        />
      </article>

      <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }} data-testid="supervisor-shift-history">
        <div className="mapper-section-heading">
          <div>
            <h3 style={{ margin: 0 }}>Shift operational history</h3>
            <p className="helper">Review authorized current and completed shifts, punches, breaks, exceptions, and segmented GPS trails.</p>
          </div>
        </div>
        {selectedLocationId ? <ShiftHistoryViewer locationId={selectedLocationId} surface="supervisor" /> : <p className="helper">Select an assigned location to view shift history.</p>}
      </article>

      <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
        <div className="mapper-section-heading">
          <h3 style={{ margin: 0 }}>Location timesheets</h3>
          <div className="inline-group">
            {(['csv', 'xlsx', 'pdf'] as const).map((format) => (
              <button key={format} type="button" className="btn btn-secondary" onClick={() => downloadExport(format)} disabled={!selectedLocationId || loading}>
                {format.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="inline-group">
          <input className="input" style={{ maxWidth: 170 }} type="date" value={timesheetFrom} onChange={(event) => setTimesheetFrom(event.target.value)} aria-label="Timesheet from" />
          <input className="input" style={{ maxWidth: 170 }} type="date" value={timesheetTo} onChange={(event) => setTimesheetTo(event.target.value)} aria-label="Timesheet to" />
          <select className="input" style={{ maxWidth: 260 }} value={workerFilter} onChange={(event) => setWorkerFilter(event.target.value)} aria-label="Worker filter">
            <option value="">All live/scheduled workers</option>
            {liveWorkers.map((worker) => {
              const id = workerId(worker)
              return id ? <option key={id} value={id}>{workerName(worker)}</option> : null
            })}
          </select>
          <button type="button" className="btn btn-primary" onClick={loadOperationalParity} disabled={!selectedLocationId || loading}>Generate</button>
        </div>
        <div className="supervisor-stats">
          <article className="panel stat-card"><strong>{timesheetSummary.length}</strong><span className="helper">Workers</span></article>
          <article className="panel stat-card"><strong>{timesheets.length}</strong><span className="helper">Shifts</span></article>
          <article className="panel stat-card"><strong>{timesheets.reduce((sum, row) => sum + row.paidHours, 0).toFixed(2)}</strong><span className="helper">Paid hours</span></article>
        </div>
        <div className="panel" style={{ overflowX: 'auto', maxHeight: 420 }}>
          <table className="workspace-table">
            <thead><tr><th>Worker</th><th>Date</th><th>In</th><th>Break</th><th>Out</th><th>Paid</th><th>Flags</th><th>Action</th></tr></thead>
            <tbody>
              {timesheets.length === 0 ? (
                <tr><td colSpan={8} className="helper">No timesheets found for this range.</td></tr>
              ) : timesheets.map((row) => (
                <tr key={row.shiftId}>
                  <td>{row.workerName}</td>
                  <td>{row.date}</td>
                  <td>{formatTime(row.punchIn)}</td>
                  <td>{row.breakMinutes ? `${row.breakMinutes} min` : '-'}</td>
                  <td>{formatTime(row.punchOut)}</td>
                  <td>{row.paidHours.toFixed(2)}</td>
                  <td>{[row.adjusted ? 'Adjusted' : '', row.approved ? 'Approved' : ''].filter(Boolean).join(', ') || '-'}</td>
                  <td><button type="button" className="btn btn-secondary" onClick={() => approveShift(row.shiftId)} disabled={row.approved || loading}>Approve</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>

      <div className="mapper-grid">
        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Messages</h3>
          <textarea className="input" rows={4} value={messageBody} onChange={(event) => setMessageBody(event.target.value)} placeholder="Message text" />
          <div className="form-row">
            <label htmlFor="directPhone">Direct phone</label>
            <input id="directPhone" className="input" value={directPhone} onChange={(event) => setDirectPhone(event.target.value)} />
          </div>
          <div className="inline-group">
            <button type="button" className="btn btn-secondary" onClick={sendBroadcast} disabled={!messageBody.trim() || loading}>Broadcast</button>
            <button type="button" className="btn btn-primary" onClick={sendDirect} disabled={!messageBody.trim() || !directPhone || loading}>Send direct</button>
          </div>
          <div className="inline-group">
            <select className="input" style={{ maxWidth: 220 }} value={messageAudience} onChange={(event) => setMessageAudience(event.target.value as any)} aria-label="Message audience">
              <option value="location">All location workers</option>
              <option value="active">Active workers</option>
              <option value="worker">Individual worker</option>
            </select>
            {messageAudience === 'worker' ? (
              <select className="input" style={{ maxWidth: 260 }} value={messageWorkerId} onChange={(event) => setMessageWorkerId(event.target.value)} aria-label="Message worker">
                <option value="">Select worker</option>
                {liveWorkers.map((worker) => {
                  const id = workerId(worker)
                  return id ? <option key={id} value={id}>{workerName(worker)}</option> : null
                })}
              </select>
            ) : null}
            <button type="button" className="btn btn-primary" onClick={sendAdminMessage} disabled={!messageBody.trim() || (messageAudience === 'worker' && !messageWorkerId) || loading}>Send admin message</button>
          </div>
          <div className="panel" style={{ overflowX: 'auto', maxHeight: 260 }}>
            <table className="workspace-table">
              <thead><tr><th>Sent</th><th>Worker</th><th>Message</th><th>Delivery</th></tr></thead>
              <tbody>
                {messageHistory.length === 0 ? (
                  <tr><td colSpan={4} className="helper">No admin messages found.</td></tr>
                ) : messageHistory.map((message) => (
                  <tr key={message.id}>
                    <td>{formatTime(message.sentAt)}</td>
                    <td>{message.workerEmail || formatPhone(message.workerPhone)}</td>
                    <td>{message.body}</td>
                    <td>{message.deliveryState || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Punch approvals</h3>
          <select className="input" value={selectedLogId} onChange={(event) => setSelectedLogId(event.target.value)}>
            <option value="">Select recent punch</option>
            {logs.map((log) => <option key={log.id} value={log.id}>{formatPhone(log.phone)} - {log.action} - {formatTime(log.timestamp)}</option>)}
          </select>
          <textarea className="input" rows={3} value={approvalNotes} onChange={(event) => setApprovalNotes(event.target.value)} placeholder="Approval notes" />
          <div className="inline-group">
            <button type="button" className="btn btn-secondary" onClick={() => approveSelected(false)} disabled={!selectedLog || loading}>Deny</button>
            <button type="button" className="btn btn-primary" onClick={() => approveSelected(true)} disabled={!selectedLog || loading}>Approve</button>
          </div>
        </article>
      </div>

      <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
        <h3 style={{ margin: 0 }}>Breadcrumb trails</h3>
        <div className="inline-group">
          <input className="input" style={{ maxWidth: 260 }} value={breadcrumbPhone} onChange={(event) => setBreadcrumbPhone(event.target.value)} placeholder="Worker phone filter" />
          <button type="button" className="btn btn-secondary" onClick={loadBreadcrumbs} disabled={!selectedLocation || loading}>Load breadcrumbs</button>
        </div>
        <div className="panel" style={{ overflowX: 'auto', maxHeight: 320 }}>
          <table className="workspace-table">
            <thead><tr><th>Worker</th><th>Phone</th><th>Recorded</th><th>Lat</th><th>Lon</th><th>Source</th></tr></thead>
            <tbody>
              {breadcrumbs.length === 0 ? (
                <tr><td colSpan={6} className="helper">No breadcrumbs loaded for this location.</td></tr>
              ) : breadcrumbs.map((breadcrumb) => (
                <tr key={breadcrumb.id || `${breadcrumb.phone}-${breadcrumb.recordedAt}`}>
                  <td>{breadcrumb.workerName || 'Unknown worker'}</td>
                  <td>{formatPhone(breadcrumb.phone)}</td>
                  <td>{formatTime(breadcrumb.recordedAt)}</td>
                  <td>{breadcrumb.lat ?? '-'}</td>
                  <td>{breadcrumb.lon ?? '-'}</td>
                  <td>{breadcrumb.source || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  )
}

export const Supervisor: React.FC = () => {
  const { accessContext } = useAccessContext()
  const { t } = useI18n()
  const [showLive, setShowLive] = useState(false)
  return <section className="stack" data-testid="supervisor-workspace">
    <SupervisorOperationsPanel accessContext={accessContext} headingLevel={1} />
    <button type="button" className="btn" aria-expanded={showLive} aria-controls="supervisor-live-map" onClick={() => setShowLive(value => !value)}>
      {t('Live map and messages')}
    </button>
    <div id="supervisor-live-map" hidden={!showLive}>
      {showLive && <SupervisorLiveMap key={JSON.stringify([accessContext?.tenantId, accessContext?.currentUser?.id, accessContext?.effectivePermissions])} />}
    </div>
  </section>
}
